import { useEffect } from 'react'

const STORAGE_KEY = 'sf_theme'

/**
 * Makes pre-auth / onboarding routes follow the OS theme (prefers-color-scheme)
 * when the user has NOT explicitly chosen a theme.
 *
 * The app's stored default is 'light' (themeStore) and the authenticated layout
 * only syncs an explicit light/dark/system preference — so a brand-new account
 * (no preference yet) would render the login / registration / onboarding screens
 * in light mode even on a dark OS. This wrapper bridges that gap for those routes
 * only.
 *
 * An explicit 'light' / 'dark' / 'system' preference is left untouched (themeStore
 * already applied it, system included). Restores the previous state on unmount so
 * it never leaks system-dark into the authenticated app, which is a light-only
 * surface today (mirrors {@link ForceLightTheme}).
 */
export function SystemTheme({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    let stored: string | null = null
    try { stored = localStorage.getItem(STORAGE_KEY) } catch { /* SSR / privacy mode */ }
    // Respect an explicit preference — themeStore owns it (and keeps 'system' in
    // sync via its own matchMedia listener).
    if (stored === 'light' || stored === 'dark' || stored === 'system') return

    const root = document.documentElement
    const wasDark = root.classList.contains('dark')
    const prevColorScheme = root.style.colorScheme
    const mq = window.matchMedia('(prefers-color-scheme: dark)')

    const apply = () => {
      if (mq.matches) {
        root.classList.add('dark')
        root.style.colorScheme = 'dark'
      } else {
        root.classList.remove('dark')
        root.style.colorScheme = 'light'
      }
    }
    apply()
    mq.addEventListener('change', apply)

    return () => {
      mq.removeEventListener('change', apply)
      if (wasDark) {
        root.classList.add('dark')
        root.style.colorScheme = 'dark'
      } else {
        root.classList.remove('dark')
        root.style.colorScheme = prevColorScheme
      }
    }
  }, [])

  return <>{children}</>
}
